import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { categoryService } from '../../services/api';
import ConfirmationModal from '../../components/ConfirmationModal';
import '../../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState({ name: '', image: null });
  const [showForm, setShowForm] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCategoryId, setConfirmCategoryId] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await categoryService.getAllCategories();
      setCategories(res.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name) {
      alert('Please enter a category name');
      return;
    }

    const formData = new FormData();
    formData.append('name', newCategory.name);
    if (newCategory.image) {
      formData.append('image', newCategory.image);
    }

    try {
      await categoryService.createCategory(formData);
      setNewCategory({ name: '', image: null });
      setShowForm(false);
      await fetchCategories();
    } catch (err) {
      alert('Error creating category: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteCategory = (id) => {
    setConfirmCategoryId(id);
    setConfirmMessage('Are you sure you want to delete this category? All reels in this category will also be deleted.');
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    if (!confirmCategoryId) return;

    try {
      await categoryService.deleteCategory(confirmCategoryId);
      setConfirmCategoryId(null);
      await fetchCategories();
    } catch (err) {
      alert('Error deleting category: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setConfirmCategoryId(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <ConfirmationModal
        isOpen={confirmOpen}
        title="Confirm Delete"
        message={confirmMessage}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-info">
          <span>Welcome, {admin?.username}</span>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="categories-section">
          <div className="section-header">
            <h2>Categories ({categories.length})</h2>
            <button
              onClick={() => setShowForm(!showForm)}
              className="add-btn"
            >
              {showForm ? '✕ Cancel' : '+ Add Category'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleCreateCategory} className="category-form">
              <div className="form-group">
                <label htmlFor="categoryName">Category Name</label>
                <input
                  id="categoryName"
                  type="text"
                  value={newCategory.name}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, name: e.target.value })
                  }
                  placeholder="Enter category name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="categoryImage">Category Image</label>
                <input
                  id="categoryImage"
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      image: e.target.files[0],
                    })
                  }
                />
              </div>
              <button type="submit" className="submit-btn">
                Create Category
              </button>
            </form>
          )}

          <div className="categories-list">
            {categories.length === 0 ? (
              <p className="no-data">No categories yet</p>
            ) : (
              categories.map((category) => (
                <div key={category.id} className="category-item">
                  {category.image_url && (
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="category-thumbnail"
                    />
                  )}
                  <div className="category-details">
                    <h3>{category.name}</h3>
                    <p className="reel-count">{category.reel_count || 0} reels</p>
                  </div>
                  <div className="category-actions">
                    <button
                      onClick={() => navigate(`/admin/category/${category.id}`)}
                      className="edit-btn"
                    >
                      Manage
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
